import { createApp } from "vue";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";
import "vuetify/dist/vuetify.min.css";
import { pinia } from "./modules/pinia";
import { msalInstance } from "@/msalAuthConfig";
import * as Sentry from "@sentry/vue";
import { waitForOffice } from "@/utils/office";
import { createMsalVuePlugin } from "@haskoning/msal/vue";

const app = createApp(App).use(vuetify).use(pinia);
app.use(createMsalVuePlugin(msalInstance));

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ["*"],
  integrations: [
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: "Feedback",
      formTitle: "Send Feedback",
      submitButtonLabel: "Send Feedback",
      cancelButtonLabel: "Cancel",
      messagePlaceholder: "What would you like to tell us?",
      successMessageText: "Thank you for your feedback!",
      colorScheme: "light",
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

waitForOffice().then(async (officeAvailable) => {
  if (officeAvailable) {
    await Office.onReady();
    app.mount("#app");

    const feedback = Sentry.getFeedback();
    if (!feedback) return;

    let widget: ReturnType<typeof feedback.createWidget> | null = null;

    const isAuthenticated = () => {
      const accounts = msalInstance.getAllAccounts();
      return accounts.length > 0;
    };

    const updateFeedbackWidget = () => {
      if (isAuthenticated() && !widget) {
        widget = feedback.createWidget();
        widget.appendToDom();

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0 && accounts[0].username) {
          Sentry.setUser({
            username: accounts[0].name ?? "",
            email: accounts[0].username ?? "",
          });
        }
      } else if (!isAuthenticated() && widget) {
        widget.removeFromDom();
        widget = null;
        Sentry.setUser(null);
      }
    };

    updateFeedbackWidget();

    // Watch for authentication state changes
    msalInstance.addEventCallback((event) => {
      if (event.eventType === "msal:loginSuccess" || event.eventType === "msal:logoutSuccess") {
        updateFeedbackWidget();
      }
    });
  } else {
    console.warn("Office.js failed to load after retries");
    app.mount("#app");
  }
});
